import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "components/Button";
import { PageWithNav } from "components/PageWithNav";
import { Spinner } from "components/Spinner";
import { SwapList } from "components/SwapList";
import api from "utils/api";
import { useToastContext } from "context/ToastContext";

const LessonManagements = ({ lessons, onChange }) => {
  const [data, setData] = useState(lessons ?? []);
  const [allLessons, setAllLessons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [hasError, setHasError] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const loadDropdownData = async () => {
    try {
      setHasError(false);
      setIsLoading(true);
      const response = await api.get("/lessons");
      setAllLessons([...response.data.lessons.map((lesson) => ({ value: lesson.id, label: lesson.name }))]);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDropdownData();
  }, []);

  useEffect(() => {
    setShowForm(!!(lessons && lessons.length));
  }, [lessons]);

  useEffect(() => {
    onChange(data.map((d, i) => ({ id: d.value, order: i })));
  }, [data, onChange]);

  if (isLoading) {
    return (
      <div className="flex justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (hasError) {
    return <p className="text-md my-3 flex justify-center font-semibold leading-3 text-red-900">Unable to load lessons, please try again. If error persists, contact the developer.</p>;
  }

  return (
    <div className="flex flex-col rounded-md border p-4">
      {!data.length && !showForm && (
        <div className="align-center flex flex-col items-center gap-4">
          <p className="text-lg font-semibold text-neutral-500">Course is empty.</p>
          <div>
            <Button size="sm" onClick={() => setShowForm(true)}>
              Attach lessons +
            </Button>
          </div>
        </div>
      )}

      {!!showForm && !!allLessons.length && <SwapList values={data} options={allLessons} selectPlaceholder="Select Lessons" onChange={setData} listTitle="Selected Lessons" />}
    </div>
  );
};

export const ManageLessons = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const { notify } = useToastContext();

  // state
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [data, setData] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [selectedLessons, setSelectedLessons] = useState([]);

  const loadLessonsData = useCallback(async () => {
    try {
      const response = await api.get(`/course/${courseId}/lessons`);
      const courseData = response.data.courseLessons;
      if (courseData) {
        setData(courseData);
        setSelectedLessons([...courseData.map((d) => ({ id: d.id, order: d.pivot.order }))]);
      }
    } catch {
      setHasError(true);
    } finally {
      setLoadingData(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadLessonsData();
  }, [loadLessonsData]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      await api.put(`/course/${courseId}/lessons`, {
        lesson_data: selectedLessons,
      });
      setIsLoading(false);
      notify({ title: "Success", description: "Course updated!", type: "success" });
      navigate("/courses");
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <PageWithNav>
      {loadingData && (
        <div className="flex justify-center">
          <Spinner size="lg" color="primary" />
        </div>
      )}
      {hasError && !isLoading && <div className="flex justify-center text-xl font-semibold leading-3 text-red-900">There was a problem, refresh the page to try again.</div>}
      {!hasError && !loadingData && (
        <form onSubmit={handleSubmit} data-testid="manage-lessons-form">
          <LessonManagements lessons={data} onChange={setSelectedLessons} />
          <div className="my-4 flex gap-2">
            <Button disabled={isLoading} onClick={() => navigate("/courses")} color="secondary" size="sm">
              Cancel
            </Button>
            <Button disabled={isLoading} loading={isLoading} type="submit" color="primary" size="sm">
              Save
            </Button>
          </div>
        </form>
      )}
    </PageWithNav>
  );
};
